/**
 * Component for the destroy mother plant workflow
 * Implements a state machine for user decision and API interaction
 */

import React from 'react';
import type { DestroyMotherState, Plant, BreedingWorkflowResult } from '../types/breeding';

interface DestroyMotherPanelProps {
  destroyState: DestroyMotherState;
  plant: Plant;
  destroyReason?: string;
  destroyReasonType?: number;
  onDestroyYes: (
    plant: Plant,
    onSuccess?: (updatedRun: Partial<BreedingWorkflowResult>) => void,
    reason?: string,
    reasonType?: number
  ) => void;
  onDestroyNo: (onDecline?: (updatedRun: Partial<BreedingWorkflowResult>) => void) => void;
  onUpdateRun?: (updates: Partial<BreedingWorkflowResult>) => void;
}

export function DestroyMotherPanel({
  destroyState,
  plant,
  destroyReason = 'Self-pollination complete',
  destroyReasonType = 1,
  onDestroyYes,
  onDestroyNo,
  onUpdateRun,
}: DestroyMotherPanelProps) {
  const handleYesClick = () => {
    onDestroyYes(plant, onUpdateRun, destroyReason, destroyReasonType);
  };

  const handleNoClick = () => {
    onDestroyNo(onUpdateRun);
  };

  const renderContent = () => {
    switch (destroyState.status) {
      case 'prompt':
        return (
          <>
            <h4 style={{ margin: '0 0 12px 0', color: '#2d3748' }}>Destroy Mother Plant?</h4>
            <p style={{ margin: '0 0 16px 0', color: '#4a5568' }}>
              Would you like to record this mother plant as destroyed in Cultivera? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button
                onClick={handleYesClick}
                className="btn-danger"
                style={{ padding: '0.75rem 1.25rem' }}
              >
                Yes, destroy mother
              </button>
              <button
                onClick={handleNoClick}
                className="btn-secondary"
                style={{ padding: '0.75rem 1.25rem' }}
              >
                No, keep mother
              </button>
            </div>
          </>
        );

      case 'confirming':
        return (
          <>
            <h4 style={{ margin: '0 0 8px 0', color: '#2d3748' }}>Destroying mother plant&</h4>
            <p style={{ margin: '0', color: '#4a5568' }}>
              Submitting destruction request to Cultivera. Please wait&
            </p>
          </>
        );

      case 'success': {
        const transactionId =
          destroyState.result?.TransactionId || destroyState.result?.transactionId;
        return (
          <>
            <h4 style={{ margin: '0 0 8px 0', color: '#22543d' }}>Mother plant destroyed</h4>
            <p style={{ margin: '0', color: '#22543d' }}>
               Successfully recorded the destruction in Cultivera
              {transactionId && ` (Transaction ID: ${transactionId})`}.
            </p>
          </>
        );
      }

      case 'declined':
        return (
          <>
            <h4 style={{ margin: '0 0 8px 0', color: '#2d3748' }}>Mother plant retained</h4>
            <p style={{ margin: '0', color: '#4a5568' }}>
              The mother plant remains active. You can close this workflow or start a new one.
            </p>
          </>
        );

      case 'error':
        return (
          <>
            <h4 style={{ margin: '0 0 8px 0', color: '#c53030' }}>Failed to destroy mother</h4>
            <p style={{ margin: '0 0 16px 0', color: '#c53030' }}>
              {destroyState.error || 'Unknown error occurred.'}
            </p>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button
                onClick={handleYesClick}
                className="btn-danger"
                style={{ padding: '0.75rem 1.25rem' }}
              >
                Try again
              </button>
              <button
                onClick={handleNoClick}
                className="btn-secondary"
                style={{ padding: '0.75rem 1.25rem' }}
              >
                Keep mother
              </button>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  if (destroyState.status === 'idle') {
    return null;
  }

  return (
    <div
      style={{
        marginTop: '24px',
        padding: '20px',
        background: '#f7fafc',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
      }}
    >
      {renderContent()}
    </div>
  );
}
